import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import TopNav from '../components/TopNav';
import Footer from '../components/Footer';
import DemoModal from '../components/DemoModal';
import PromoBanner from '../components/PromoBanner';
import { useAuthDrawer } from '../context/AuthDrawerContext';

const LANDING_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Inter:wght@400;500;600;700;800&display=swap');

  @keyframes ckGradShift { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }

  .ck-landing { font-family: 'Inter', sans-serif; background: #f8faff; color: #0f172a; overflow-x: hidden; }

  /* Anchor sections need scroll offset for sticky nav */
  .ck-landing [id] { scroll-margin-top: 72px; }

  /* ── HERO (Cred-inspired: bold, editorial, two-column) ─── */
  .ck-hero {
    position: relative;
    padding: 5rem 2rem 6rem;
    overflow: hidden;
    background: #fafafa;
  }
  .ck-hero-inner {
    max-width: 1240px; margin: 0 auto;
    display: grid;
    grid-template-columns: 1.15fr 0.85fr;
    gap: 4rem;
    align-items: center;
    position: relative; z-index: 1;
  }
  .ck-hero-blob {
    position: absolute; border-radius: 50%; filter: blur(90px); pointer-events: none; z-index: 0;
    opacity: 0.6;
  }
  .ck-hero-eyebrow {
    display: inline-flex; align-items: center; gap: 0.55rem;
    padding: 0.5rem 1rem;
    border-radius: 999px;
    background: rgba(10,10,12,0.04);
    border: 1px solid rgba(10,10,12,0.08);
    font-size: 0.95rem; font-weight: 600;
    color: #27272a;
    margin-bottom: 1.5rem;
    letter-spacing: 0.01em;
  }
  .ck-hero-eyebrow .dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: #16a34a;
    box-shadow: 0 0 0 3px rgba(22,163,74,0.18);
  }
  .ck-hero h1 {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: clamp(2.6rem, 5vw, 4.6rem);
    line-height: 1;
    letter-spacing: -0.04em;
    color: #0a0a0c;
    margin: 0 0 1.5rem;
    text-wrap: balance;
  }
  .ck-hero h1 .grad {
    background: linear-gradient(90deg,#6366f1,#ec4899,#f59e0b);
    background-size: 200%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: ckGradShift 5s ease infinite;
  }
  .ck-hero p.sub {
    font-size: clamp(1.05rem, 1.3vw, 1.18rem);
    color: #52525b;
    line-height: 1.55;
    max-width: 540px;
    margin: 0 0 2rem;
  }
  .ck-hero-cta-row {
    display: flex; gap: 1.25rem; align-items: center;
    flex-wrap: wrap;
    margin-bottom: 1.25rem;
  }
  .ck-cta-primary {
    border: none; cursor: pointer;
    text-decoration: none; color: white;
    font-family: 'Inter', sans-serif;
    font-size: 1.02rem; font-weight: 700;
    padding: 1.05rem 1.8rem; border-radius: 12px;
    background: #0a0a0c;
    transition: transform 0.15s, box-shadow 0.15s, background 0.15s;
    display: inline-flex; align-items: center; gap: 0.5rem;
    letter-spacing: -0.01em;
  }
  .ck-cta-primary:hover {
    background: #18181b;
    transform: translateY(-2px);
    box-shadow: 0 14px 32px rgba(10,10,12,0.22);
  }

  .ck-cta-watch {
    text-decoration: none;
    background: none; border: none; cursor: pointer;
    color: #18181b;
    font-family: 'Inter', sans-serif;
    font-size: 0.98rem; font-weight: 600;
    padding: 0.5rem 0.2rem;
    transition: opacity 0.15s, transform 0.15s;
    display: inline-flex; align-items: center; gap: 0.65rem;
  }
  .ck-cta-watch:hover { opacity: 0.7; transform: translateX(2px); }
  .ck-cta-watch .play {
    width: 36px; height: 36px; border-radius: 50%;
    background: white;
    border: 1.5px solid #e4e4e7;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 0.7rem; color: #0a0a0c;
    transition: background 0.15s, border-color 0.15s;
  }
  .ck-cta-watch:hover .play {
    background: #0a0a0c; color: white; border-color: #0a0a0c;
  }

  .ck-trust {
    margin-top: 0.5rem;
    font-size: 0.85rem;
    color: #a1a1aa;
    display: inline-flex; align-items: center; gap: 0.6rem;
  }
  .ck-trust .pill {
    display: inline-flex; align-items: center; gap: 0.3rem;
    color: #71717a; font-weight: 600;
  }
  .ck-trust .sep { color: #d4d4d8; }

  /* ── HERO MOCKUP (right column) ────────────────────────── */
  .ck-hero-visual {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    min-width: 0;
  }
  .ck-mockup {
    background: white;
    border: 1px solid #e4e4e7;
    border-radius: 20px;
    padding: 1.5rem;
    width: 100%; max-width: 440px;
    box-shadow:
      0 1px 2px rgba(10,10,12,0.04),
      0 8px 24px rgba(10,10,12,0.06),
      0 24px 48px rgba(10,10,12,0.06);
    position: relative;
    z-index: 2;
  }
  .ck-mockup-head {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 1.1rem;
  }
  .ck-mockup-title {
    font-family: 'Syne', sans-serif;
    font-weight: 800; font-size: 0.95rem;
    color: #0a0a0c; letter-spacing: -0.01em;
  }
  .ck-mockup-tag {
    font-size: 0.66rem; font-weight: 700;
    padding: 0.22rem 0.55rem;
    border-radius: 999px;
    background: #ecfdf5; color: #047857;
    letter-spacing: 0.06em;
  }
  .ck-mockup-row {
    display: flex; align-items: center; justify-content: space-between;
    padding: 0.85rem 0;
    border-top: 1px solid #f4f4f5;
  }
  .ck-mockup-row:first-of-type { border-top: none; }
  .ck-mockup-row-left { display: flex; align-items: center; gap: 0.75rem; }
  .ck-mockup-icon {
    width: 36px; height: 36px; border-radius: 10px;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 0.95rem;
    flex-shrink: 0;
  }
  .ck-mockup-icon.green  { background: #ecfdf5; }
  .ck-mockup-icon.purple { background: #f5f3ff; }
  .ck-mockup-icon.amber  { background: #fffbeb; }
  .ck-mockup-icon.pink   { background: #fdf2f8; }
  .ck-mockup-label  { font-size: 0.88rem; font-weight: 600; color: #0a0a0c; line-height: 1.2; }
  .ck-mockup-sub    { font-size: 0.72rem; color: #a1a1aa; margin-top: 2px; }
  .ck-mockup-amount { font-size: 0.92rem; font-weight: 700; color: #0a0a0c; letter-spacing: -0.01em; }
  .ck-mockup-amount.pos { color: #047857; }
  .ck-mockup-pill {
    display: inline-block;
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    padding: 2px 6px;
    border-radius: 4px;
    margin-bottom: 4px;
    text-transform: uppercase;
  }
  .ck-mockup-pill.green  { background: #ecfdf5; color: #047857; }
  .ck-mockup-pill.purple { background: #f5f3ff; color: #6d28d9; }
  .ck-mockup-pill.amber  { background: #fffbeb; color: #b45309; }
  .ck-mockup-pill.pink   { background: #fdf2f8; color: #be185d; }

  .ck-mockup-total {
    margin-top: 1rem;
    padding: 0.9rem 1rem;
    background: linear-gradient(135deg, #0a0a0c 0%, #312e81 100%);
    border-radius: 12px;
    color: white;
    display: flex; align-items: center; justify-content: space-between;
  }
  .ck-mockup-total-label { font-size: 0.78rem; color: rgba(255,255,255,0.7); font-weight: 500; }
  .ck-mockup-total-value {
    font-family: 'Syne', sans-serif;
    font-weight: 800; font-size: 1.2rem;
    letter-spacing: -0.02em;
  }

  /* ── REPLACES STRIP ────────────────────────────────────── */
  .ck-replaces {
    padding: 1.5rem 2rem;
    background: white;
    border-top: 1px solid #f1f5f9;
    border-bottom: 1px solid #f1f5f9;
  }
  .ck-replaces-inner {
    max-width: 1100px; margin: 0 auto;
    display: flex; align-items: center; justify-content: center;
    gap: 1.25rem; flex-wrap: wrap;
  }
  .ck-replaces-label {
    font-size: 0.78rem; font-weight: 700;
    color: #94a3b8; text-transform: uppercase; letter-spacing: 0.12em;
  }
  .ck-replaces-list {
    display: flex; gap: 1.75rem; flex-wrap: wrap;
    align-items: center; justify-content: center;
  }
  .ck-replaces-item {
    font-size: 1rem; font-weight: 600;
    color: #27272a;
    position: relative;
  }
  .ck-replaces-item.strike { text-decoration: line-through; text-decoration-color: rgba(236,72,153,0.85); text-decoration-thickness: 2px; }

  /* ── PROBLEM ───────────────────────────────────────────── */
  .ck-problem { padding: 5rem 2rem; }
  .ck-section-head { max-width: 800px; margin: 0 auto 3rem; text-align: center; }
  .ck-section-eyebrow {
    display: inline-block;
    font-size: 0.75rem; font-weight: 700;
    color: #ec4899; text-transform: uppercase; letter-spacing: 0.14em;
    margin-bottom: 0.75rem;
  }
  .ck-section-head h2 {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: clamp(1.9rem, 3.6vw, 2.8rem);
    line-height: 1.15;
    letter-spacing: -0.03em;
    color: #0f172a;
    margin: 0 0 1rem;
  }
  .ck-section-head p {
    font-size: 1.05rem;
    color: #475569;
    line-height: 1.65;
  }

  .ck-problem-grid {
    max-width: 1100px; margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.25rem;
  }
  @media (max-width: 900px) {
    .ck-problem-grid { grid-template-columns: repeat(2, 1fr); }
  }
  @media (max-width: 600px) {
    .ck-problem-grid { grid-template-columns: 1fr; }
  }
  .ck-problem-card {
    background: white;
    border: 1px solid #f1f5f9;
    border-radius: 16px;
    padding: 1.5rem;
    transition: transform 0.18s, box-shadow 0.18s, border-color 0.18s;
  }
  .ck-problem-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 28px rgba(15,23,42,0.06);
    border-color: #e2e8f0;
  }
  .ck-problem-icon {
    font-size: 1.8rem;
    margin-bottom: 0.75rem;
    display: inline-block;
  }
  .ck-problem-card h3 {
    font-family: 'Inter', sans-serif;
    font-weight: 700;
    font-size: 1.05rem;
    color: #0f172a;
    margin: 0 0 0.4rem;
  }
  .ck-problem-card p {
    font-size: 0.9rem;
    line-height: 1.55;
    color: #64748b;
    margin: 0;
  }

  /* ── FEATURES ──────────────────────────────────────────── */
  .ck-features { padding: 5rem 2rem; background: linear-gradient(180deg, #f8faff 0%, #ffffff 100%); }
  .ck-features-grid {
    max-width: 1200px; margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
  }
  .ck-features-col {
    background: white;
    border: 1px solid #f1f5f9;
    border-radius: 20px;
    padding: 2rem;
  }
  .ck-features-col.pro {
    background: linear-gradient(180deg, #faf5ff 0%, #fff 100%);
    border-color: rgba(139,92,246,0.2);
    position: relative;
  }
  .ck-features-col-head {
    display: flex; align-items: center; gap: 0.6rem;
    margin-bottom: 1.5rem;
  }
  .ck-features-tag {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 0.7rem;
    padding: 0.3rem 0.7rem;
    border-radius: 999px;
    letter-spacing: 0.1em;
  }
  .ck-features-tag.free {
    background: #f1f5f9;
    color: #475569;
  }
  .ck-features-tag.pro {
    background: linear-gradient(135deg,#6366f1,#ec4899);
    color: white;
    box-shadow: 0 3px 10px rgba(99,102,241,0.25);
  }
  .ck-features-col h3 {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 1.4rem;
    letter-spacing: -0.02em;
    color: #0f172a;
    margin: 0;
  }
  .ck-features-col-sub {
    font-size: 0.88rem; color: #64748b;
    margin: 0 0 1.5rem;
  }
  .ck-features-list {
    list-style: none; padding: 0; margin: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.6rem 1rem;
  }
  .ck-features-list li {
    display: flex; align-items: flex-start; gap: 0.5rem;
    font-size: 0.92rem; color: #334155;
  }
  .ck-features-list li .fcheck {
    flex-shrink: 0;
    width: 18px; height: 18px;
    border-radius: 999px;
    background: #ecfdf5;
    color: #059669;
    font-size: 0.7rem; font-weight: 800;
    display: flex; align-items: center; justify-content: center;
    margin-top: 1px;
  }
  .ck-features-col.pro .ck-features-list li .fcheck {
    background: linear-gradient(135deg,rgba(99,102,241,0.15),rgba(236,72,153,0.15));
    color: #8b5cf6;
  }

  /* ── HOW IT WORKS ──────────────────────────────────────── */
  .ck-how { padding: 5rem 2rem; }
  .ck-how-grid {
    max-width: 1100px; margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
    position: relative;
  }
  .ck-how-step {
    background: white;
    border: 1px solid #f1f5f9;
    border-radius: 20px;
    padding: 2rem 1.75rem;
    position: relative;
    transition: transform 0.2s, box-shadow 0.2s;
  }
  .ck-how-step:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 36px rgba(15,23,42,0.07);
  }
  .ck-how-num {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 2.4rem;
    background: linear-gradient(135deg,#6366f1,#ec4899);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    line-height: 1;
    margin-bottom: 0.5rem;
  }
  .ck-how-step h3 {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 1.25rem;
    color: #0f172a;
    margin: 0 0 0.5rem;
    letter-spacing: -0.02em;
  }
  .ck-how-step p {
    font-size: 0.92rem;
    color: #64748b;
    line-height: 1.6;
    margin: 0 0 1rem;
  }
  .ck-how-tools {
    display: flex; flex-wrap: wrap; gap: 0.35rem;
  }
  .ck-how-tool {
    font-size: 0.72rem; font-weight: 600;
    padding: 0.25rem 0.6rem;
    border-radius: 999px;
    background: #f1f5f9;
    color: #475569;
  }
  .ck-how-cta-row {
    max-width: 1100px; margin: 2.5rem auto 0;
    display: flex; justify-content: center; gap: 1.25rem; flex-wrap: wrap;
    align-items: center;
  }
  .ck-cta-howfree {
    border: 1.5px solid #e4e4e7; cursor: pointer;
    background: white;
    font-family: 'Inter', sans-serif;
    font-size: 0.98rem; font-weight: 700;
    color: #0a0a0c;
    padding: 0.85rem 1.4rem; border-radius: 12px;
    transition: all 0.15s;
    letter-spacing: -0.01em;
  }
  .ck-cta-howfree:hover {
    background: #0a0a0c; color: white; border-color: #0a0a0c;
    transform: translateY(-1px);
    box-shadow: 0 8px 22px rgba(10,10,12,0.18);
  }

  /* ── INFO LINKS STRIP (above footer) ───────────────────── */
  .ck-info-strip {
    background: white;
    border-top: 1px solid #f1f5f9;
    padding: 1.5rem 2rem;
  }
  .ck-info-strip-inner {
    max-width: 1100px; margin: 0 auto;
    display: flex; align-items: center; justify-content: space-between;
    gap: 1.25rem; flex-wrap: wrap;
  }
  .ck-info-label {
    font-size: 0.78rem; font-weight: 700;
    color: #94a3b8; text-transform: uppercase; letter-spacing: 0.12em;
  }
  .ck-info-links {
    display: flex; gap: 0.6rem 1.4rem; flex-wrap: wrap;
    align-items: center;
  }
  .ck-info-links a {
    text-decoration: none;
    color: #475569;
    font-size: 0.88rem;
    font-weight: 600;
    transition: color 0.15s;
  }
  .ck-info-links a:hover { color: #6366f1; }
  .ck-info-sep { color: #e2e8f0; user-select: none; }

  /* ── PRICING TEASER ────────────────────────────────────── */
  .ck-pricing-teaser { padding: 5rem 2rem; background: linear-gradient(180deg, #ffffff 0%, #f8faff 100%); }
  .ck-pricing-grid {
    max-width: 900px; margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
  }
  .ck-price-card {
    background: white;
    border: 1.5px solid #f1f5f9;
    border-radius: 20px;
    padding: 2rem;
    display: flex; flex-direction: column;
    transition: transform 0.18s, box-shadow 0.18s;
  }
  .ck-price-card.featured {
    border-color: transparent;
    background: linear-gradient(white, white) padding-box,
                linear-gradient(135deg,#6366f1,#ec4899) border-box;
    border: 1.5px solid transparent;
    box-shadow: 0 12px 32px rgba(99,102,241,0.12);
    position: relative;
  }
  .ck-price-card.featured::before {
    content: 'MOST POPULAR';
    position: absolute;
    top: -10px; right: 24px;
    background: linear-gradient(135deg,#6366f1,#ec4899);
    color: white;
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 0.65rem;
    letter-spacing: 0.12em;
    padding: 0.3rem 0.7rem;
    border-radius: 999px;
    box-shadow: 0 4px 12px rgba(99,102,241,0.3);
  }
  .ck-price-card:hover { transform: translateY(-3px); }
  .ck-price-name {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 1.2rem;
    color: #0f172a;
    letter-spacing: -0.02em;
    margin: 0 0 0.4rem;
  }
  .ck-price-tagline {
    font-size: 0.85rem;
    color: #64748b;
    margin: 0 0 1.25rem;
  }
  .ck-price-amount {
    display: flex; align-items: baseline; gap: 0.3rem;
    margin-bottom: 0.3rem;
  }
  .ck-price-amount .num {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 2.4rem;
    color: #0f172a;
    letter-spacing: -0.02em;
  }
  .ck-price-amount .per { font-size: 0.9rem; color: #94a3b8; }
  .ck-price-yearly { font-size: 0.82rem; color: #ec4899; font-weight: 600; margin-bottom: 1.5rem; }
  .ck-price-features {
    list-style: none; padding: 0; margin: 0 0 1.5rem;
    display: flex; flex-direction: column; gap: 0.55rem;
    flex: 1;
  }
  .ck-price-features li {
    display: flex; align-items: flex-start; gap: 0.5rem;
    font-size: 0.9rem; color: #475569;
  }
  .ck-price-features li::before {
    content: '✓';
    color: #059669;
    font-weight: 800;
    flex-shrink: 0;
  }
  .ck-price-card.featured .ck-price-features li::before { color: #8b5cf6; }
  .ck-price-cta {
    border: none; cursor: pointer;
    width: 100%;
    text-align: center;
    text-decoration: none;
    font-family: 'Inter', sans-serif;
    font-weight: 700;
    font-size: 0.95rem;
    padding: 0.85rem 1.2rem;
    border-radius: 12px;
    transition: all 0.15s;
  }
  .ck-price-cta.free {
    background: #f1f5f9;
    color: #0f172a;
  }
  .ck-price-cta.free:hover { background: #e2e8f0; }
  .ck-price-cta.pro {
    background: linear-gradient(270deg,#6366f1,#8b5cf6,#ec4899,#8b5cf6,#6366f1);
    background-size: 300% 300%;
    animation: ckGradShift 5s ease infinite;
    color: white;
    box-shadow: 0 6px 18px rgba(99,102,241,0.3);
  }
  .ck-price-cta.pro:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(99,102,241,0.4); }
  .ck-pricing-link {
    text-align: center;
    margin-top: 2rem;
  }
  .ck-pricing-link a {
    color: #6366f1;
    font-weight: 600;
    font-size: 0.92rem;
    text-decoration: none;
  }
  .ck-pricing-link a:hover { text-decoration: underline; }

  /* ── REFER & EARN (launch promo companion) ─────────────── */
  .ck-referpub {
    padding: 5rem 2rem;
    background: linear-gradient(180deg, #fff7ed 0%, #fef3c7 100%);
    text-align: center;
  }
  .ck-referpub-inner {
    max-width: 920px; margin: 0 auto;
  }
  .ck-referpub-title {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: clamp(2.4rem, 5vw, 3.6rem);
    letter-spacing: -0.025em;
    color: #0f172a;
    margin: 0 0 0.4rem;
    line-height: 1.05;
  }
  .ck-referpub-tagline {
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: clamp(1.2rem, 2.4vw, 1.65rem);
    color: #7c2d12;
    margin: 0 0 0.85rem;
    letter-spacing: -0.01em;
  }
  .ck-referpub-sub {
    font-size: 1rem; color: #475569; line-height: 1.6;
    max-width: 620px; margin: 0 auto 2rem;
  }
  .ck-referpub-tiers {
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 1rem; margin: 0 auto 2rem;
    max-width: 760px;
  }
  .ck-referpub-tier {
    background: white;
    border: 1.5px solid #fcd34d;
    border-radius: 14px;
    padding: 1.5rem 1rem;
    box-shadow: 0 4px 14px rgba(15,23,42,0.05);
  }
  .ck-referpub-num {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 2.6rem;
    line-height: 1;
    background: linear-gradient(135deg, #f59e0b, #ec4899);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    margin-bottom: 0.4rem;
  }
  .ck-referpub-label {
    font-size: 0.85rem; color: #78350f; font-weight: 600;
    margin-bottom: 0.65rem;
  }
  .ck-referpub-reward {
    font-size: 0.95rem; font-weight: 700; color: #0f172a;
  }
  .ck-referpub-howto {
    max-width: 640px; margin: 0 auto 1.5rem;
    font-size: 0.92rem; color: #52525b; line-height: 1.65;
    text-align: left;
    background: rgba(255,255,255,0.6);
    border-radius: 12px;
    padding: 1rem 1.2rem;
    border: 1px solid rgba(252, 211, 77, 0.6);
  }
  @media (max-width: 760px) {
    .ck-referpub-tiers { grid-template-columns: 1fr; max-width: 380px; }
    .ck-referpub { padding: 3.5rem 1.25rem; }
  }

  /* ── FINAL CTA ─────────────────────────────────────────── */
  .ck-final-cta {
    padding: 5rem 2rem;
    background: linear-gradient(135deg, #0f172a 0%, #312e81 50%, #831843 100%);
    color: white;
    text-align: center;
    position: relative;
    overflow: hidden;
  }
  .ck-final-cta::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 30% 30%, rgba(99,102,241,0.25) 0%, transparent 50%),
                radial-gradient(circle at 70% 70%, rgba(236,72,153,0.2) 0%, transparent 50%);
    pointer-events: none;
  }
  .ck-final-cta-inner {
    max-width: 720px; margin: 0 auto;
    position: relative;
    z-index: 1;
  }
  .ck-final-cta h2 {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: clamp(2.2rem, 4.5vw, 3.4rem);
    line-height: 1.1;
    letter-spacing: -0.03em;
    margin: 0 0 1.25rem;
  }
  .ck-final-cta h2 .grad {
    background: linear-gradient(90deg,#a5b4fc,#f9a8d4,#fcd34d);
    background-size: 200%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: ckGradShift 5s ease infinite;
  }
  .ck-final-cta p {
    font-size: 1.08rem;
    color: rgba(255,255,255,0.78);
    line-height: 1.65;
    max-width: 560px;
    margin: 0 auto 2rem;
  }
  .ck-final-cta-row {
    display: flex; gap: 0.75rem; justify-content: center; flex-wrap: wrap;
  }
  .ck-final-primary {
    border: none; cursor: pointer;
    text-decoration: none; color: #0f172a;
    font-family: 'Inter', sans-serif;
    font-size: 1rem; font-weight: 700;
    padding: 0.95rem 1.8rem; border-radius: 12px;
    background: white;
    box-shadow: 0 10px 30px rgba(255,255,255,0.15);
    transition: transform 0.15s, box-shadow 0.15s;
    display: inline-flex; align-items: center; gap: 0.45rem;
  }
  .ck-final-primary:hover { transform: translateY(-2px); box-shadow: 0 14px 36px rgba(255,255,255,0.25); }
  .ck-final-secondary {
    text-decoration: none; color: white;
    font-size: 1rem; font-weight: 600;
    padding: 0.95rem 1.6rem; border-radius: 12px;
    background: rgba(255,255,255,0.08);
    border: 1.5px solid rgba(255,255,255,0.2);
    transition: all 0.15s;
  }
  .ck-final-secondary:hover { background: rgba(255,255,255,0.14); border-color: rgba(255,255,255,0.4); }

  /* ── RESPONSIVE ────────────────────────────────────────── */
  @media (max-width: 1000px) {
    .ck-hero-inner {
      grid-template-columns: minmax(0, 1fr);
      gap: 3rem;
    }
    .ck-hero-visual { min-height: auto; order: 2; }
    .ck-hero-text  { min-width: 0; }
  }
  @media (max-width: 900px) {
    .ck-features-grid { grid-template-columns: 1fr; }
    .ck-how-grid { grid-template-columns: 1fr; }
    .ck-pricing-grid { grid-template-columns: 1fr; }
  }
  @media (max-width: 600px) {
    .ck-hero { padding: 3rem 1rem 3.5rem; }
    .ck-hero-inner { width: 100%; max-width: 100%; }
    .ck-hero h1 { font-size: clamp(1.75rem, 8vw, 2.4rem); letter-spacing: -0.03em; line-height: 1.05; overflow-wrap: break-word; }
    .ck-hero p.sub { font-size: 1rem; max-width: none; }
    .ck-hero-eyebrow { font-size: 0.85rem; padding: 0.45rem 0.9rem; }
    .ck-hero-cta-row { gap: 0.85rem; width: 100%; }
    .ck-cta-primary { width: 100%; justify-content: center; padding: 1rem 1.4rem; }
    .ck-trust { flex-wrap: wrap; row-gap: 0.35rem; }

    .ck-mockup {
      max-width: 100%;
      padding: 1.1rem;
      border-radius: 16px;
    }
    .ck-mockup-icon { width: 30px; height: 30px; font-size: 0.85rem; border-radius: 8px; }
    .ck-mockup-label { font-size: 0.8rem; }
    .ck-mockup-sub   { font-size: 0.66rem; }
    .ck-mockup-amount { font-size: 0.82rem; }
    .ck-mockup-row { padding: 0.7rem 0; }
    .ck-mockup-row-left { gap: 0.55rem; min-width: 0; flex: 1; }
    .ck-mockup-row-left > div { min-width: 0; }
    .ck-mockup-total-value { font-size: 1.05rem; }
    .ck-mockup-total-label { font-size: 0.72rem; }

    .ck-problem, .ck-features, .ck-how, .ck-pricing-teaser, .ck-final-cta { padding: 3.5rem 1.25rem; }
    .ck-features-col, .ck-how-step, .ck-price-card { padding: 1.5rem; }
    .ck-features-list { grid-template-columns: 1fr; }
    .ck-replaces { padding: 1.25rem 1rem; }
    .ck-replaces-list { gap: 1rem; }
  }
`;

const FREE_FEATURES = [
  { label: 'Deals tracker', desc: 'Pipeline from inquiry to paid' },
  { label: 'Post planner', desc: 'Calendar across platforms' },
  { label: 'Idea bank', desc: 'Save & rank video ideas' },
  { label: 'AI Captions', desc: 'YT, Insta, X in one click' },
  { label: 'Trending now', desc: 'What\'s working today' },
  { label: 'AI co-pilot', desc: 'Scripts, hooks, titles' },
  { label: 'Public bio page', desc: 'Your link-in-bio' },
  { label: 'Analytics', desc: 'Track what works' },
];

const PRO_FEATURES = [
  { label: 'Invoices', desc: 'GST-ready, auto-numbered' },
  { label: 'Contracts', desc: 'One-click brand contracts' },
  { label: 'Payment tracker', desc: 'Chase every rupee' },
  { label: 'Rate card', desc: 'Stop undervaluing yourself' },
  { label: 'Media kit', desc: 'Pitch-ready PDF in seconds' },
  { label: 'Outreach', desc: 'Cold-pitch brands at scale' },
  { label: 'Collab manager', desc: 'Multi-creator deals' },
  { label: 'Repurpose', desc: 'One video → 10 posts' },
];

const SHOW_DEMO_BUTTON = true;

export default function LandingPage() {
  const [demoOpen, setDemoOpen] = useState(false);
  const openDemo  = () => setDemoOpen(true);
  const closeDemo = () => setDemoOpen(false);

  const { openSignIn, openSignUp } = useAuthDrawer();
  const [params, setParams] = useSearchParams();

  // Honor ?auth=signin / ?auth=signup (used by /login redirect and protected-route bounces)
  // Also capture ?ref=CODE — store in localStorage so the next signup attaches
  // referred_by on the backend. Cleaned from URL so it doesn't stick.
  useEffect(() => {
    const intent = params.get('auth');
    if (intent === 'signin')      openSignIn();
    else if (intent === 'signup') openSignUp();

    const ref = (params.get('ref') || '').trim().toUpperCase();
    if (ref) {
      try { localStorage.setItem('ck_referral_code', ref); } catch (e) { /* private mode */ }
    }

    if (intent || ref) {
      const next = new URLSearchParams(params);
      next.delete('auth');
      next.delete('ref');
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <style>{LANDING_CSS}</style>
      <div className="ck-landing">
        <TopNav />
        <PromoBanner />

        {/* ── HERO ─────────────────────────────────────────────────── */}
        <section className="ck-hero">
          <div className="ck-hero-blob" style={{ width: 520, height: 520, background: 'radial-gradient(circle, rgba(165,180,252,0.35) 0%, transparent 65%)', top: '-160px', left: '-140px' }} />
          <div className="ck-hero-blob" style={{ width: 460, height: 460, background: 'radial-gradient(circle, rgba(249,168,212,0.28) 0%, transparent 65%)', bottom: '-160px', right: '-100px' }} />

          <div className="ck-hero-inner">
            {/* Left column — text */}
            <div className="ck-hero-text">
              <span className="ck-hero-eyebrow">
                <span className="dot" />
                Built for Indian creators · Made in India
              </span>

              <h1>
                Stop leaving<br />
                <span className="grad">money on the table.</span>
              </h1>

              <p className="sub">
                The all-in-one business app for Indian creators. From brand pitch to paid invoice, in one workflow.
              </p>

              <div className="ck-hero-cta-row">
                <button type="button" className="ck-cta-primary" onClick={openSignUp}>
                  Get started — it's free →
                </button>
                {SHOW_DEMO_BUTTON && (
                  <button type="button" className="ck-cta-watch" onClick={openDemo}>
                    <span className="play">▶</span>
                    Watch product demo
                  </button>
                )}
              </div>

              <div className="ck-trust">
                <span className="pill">Just try it — no card needed</span>
              </div>
            </div>

            {/* Right column — product mockup */}
            <div className="ck-hero-visual" aria-hidden="true">
              <div className="ck-mockup">
                <div className="ck-mockup-head">
                  <span className="ck-mockup-title">This month · {new Date().toLocaleString('en-IN', { month: 'long' })}</span>
                  <span className="ck-mockup-tag">LIVE</span>
                </div>

                <div className="ck-mockup-row">
                  <div className="ck-mockup-row-left">
                    <span className="ck-mockup-icon green">🧾</span>
                    <div>
                      <span className="ck-mockup-pill green">Invoicing</span>
                      <div className="ck-mockup-label">MamaEarth — Invoice paid</div>
                      <div className="ck-mockup-sub">Instagram Reel · GST 18%</div>
                    </div>
                  </div>
                  <div className="ck-mockup-amount pos">+₹1,20,000</div>
                </div>

                <div className="ck-mockup-row">
                  <div className="ck-mockup-row-left">
                    <span className="ck-mockup-icon purple">📄</span>
                    <div>
                      <span className="ck-mockup-pill purple">Contracts</span>
                      <div className="ck-mockup-label">Nykaa — Contract sent</div>
                      <div className="ck-mockup-sub">YouTube Shorts · Awaiting sign</div>
                    </div>
                  </div>
                  <div className="ck-mockup-amount">₹85,000</div>
                </div>

                <div className="ck-mockup-row">
                  <div className="ck-mockup-row-left">
                    <span className="ck-mockup-icon amber">💸</span>
                    <div>
                      <span className="ck-mockup-pill amber">TDS Tracking</span>
                      <div className="ck-mockup-label">TDS deducted (194J)</div>
                      <div className="ck-mockup-sub">Logged for FY 25-26 ITR</div>
                    </div>
                  </div>
                  <div className="ck-mockup-amount">−₹8,200</div>
                </div>

                <div className="ck-mockup-row">
                  <div className="ck-mockup-row-left">
                    <span className="ck-mockup-icon pink">🤖</span>
                    <div>
                      <span className="ck-mockup-pill pink">AI Ideas</span>
                      <div className="ck-mockup-label">AI generated 5 ideas</div>
                      <div className="ck-mockup-sub">Niche: finance · India</div>
                    </div>
                  </div>
                  <div className="ck-mockup-amount" style={{ color: '#a1a1aa', fontSize: '0.78rem' }}>now</div>
                </div>

                <div className="ck-mockup-total">
                  <span className="ck-mockup-total-label">Net collected this month</span>
                  <span className="ck-mockup-total-value">₹3,12,450</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── PRODUCT HUNT COMING-SOON BADGE ───────────────────────── */}
        <section
          aria-label="We're launching on Product Hunt"
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '1.25rem 1.5rem 0.25rem',
            background: 'var(--off-white, #f8f9fb)',
          }}
        >
          <a
            href="https://www.producthunt.com/products/creatorkhata?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-creatorkhata"
            target="_blank"
            rel="noopener noreferrer"
            style={{ lineHeight: 0 }}
          >
            <img
              alt="CreatorKhata - Every brand deal. Every rupee. Built for Indian creators. | Product Hunt"
              width="250"
              height="54"
              src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1150432&theme=neutral"
            />
          </a>
        </section>

        {/* ── REPLACES STRIP ───────────────────────────────────────── */}
        <section className="ck-replaces">
          <div className="ck-replaces-inner">
            <span className="ck-replaces-label">Replaces</span>
            <div className="ck-replaces-list">
              <span className="ck-replaces-item strike">Excel sheets</span>
              <span className="ck-replaces-item strike">Notion docs</span>
              <span className="ck-replaces-item strike">WhatsApp chats</span>
              <span className="ck-replaces-item strike">Email threads</span>
              <span className="ck-replaces-item strike">ChatGPT tabs</span>
              <span className="ck-replaces-item strike">Canva templates</span>
            </div>
          </div>
        </section>

        {/* ── PROBLEM ──────────────────────────────────────────────── */}
        <section className="ck-problem">
          <div className="ck-section-head">
            <div className="ck-section-eyebrow">The hidden tax on every creator</div>
            <h2>You're leaving money on the table because…</h2>
            <p>Every Indian creator loses ₹50k–₹5L a year to bad systems. Not bad work — bad systems.</p>
          </div>
          <div className="ck-problem-grid">
            <div className="ck-problem-card">
              <div className="ck-problem-icon">💸</div>
              <h3>Invoices fall through the cracks</h3>
              <p>Deal closes, video goes live, money never collected. No follow-up, no reminder, no record.</p>
            </div>
            <div className="ck-problem-card">
              <div className="ck-problem-icon">📉</div>
              <h3>Tax deductions get missed</h3>
              <p>Camera gear, software, freelancers — half of it never makes it to your CA. You overpay GST every quarter.</p>
            </div>
            <div className="ck-problem-card">
              <div className="ck-problem-icon">💼</div>
              <h3>You undervalue your own deals</h3>
              <p>No rate card means you're guessing on every brand call. The brand always knows. You don't.</p>
            </div>
            <div className="ck-problem-card">
              <div className="ck-problem-icon">🤯</div>
              <h3>Your tools are scattered</h3>
              <p>Deal in WhatsApp, brief in email, contract in PDF, invoice in Excel, expenses in head. Nothing connects.</p>
            </div>
            <div className="ck-problem-card">
              <div className="ck-problem-icon">⏰</div>
              <h3>Follow-ups never happen</h3>
              <p>Brand goes quiet for 3 weeks. Deal dies. You blame the brand — but no one set a reminder.</p>
            </div>
            <div className="ck-problem-card">
              <div className="ck-problem-icon">🎯</div>
              <h3>Cold pitches go nowhere</h3>
              <p>No outreach pipeline, no media kit ready, no rate card to attach. So you don't pitch. You wait.</p>
            </div>
          </div>
        </section>

        {/* ── FEATURES ─────────────────────────────────────────────── */}
        <section className="ck-features" id="features">
          <div className="ck-section-head">
            <div className="ck-section-eyebrow">Everything in one app</div>
            <h2>Free for the workflow.<br />PRO for the money.</h2>
            <p>Most tools either help you make content or run the business. We do both — that's the whole point.</p>
          </div>

          <div className="ck-features-grid">
            <div className="ck-features-col">
              <div className="ck-features-col-head">
                <span className="ck-features-tag free">FREE</span>
              </div>
              <h3>Content workflow</h3>
              <p className="ck-features-col-sub">Plan, create, and post — all in one place.</p>
              <ul className="ck-features-list">
                {FREE_FEATURES.map(f => (
                  <li key={f.label}>
                    <span className="fcheck">✓</span>
                    <div>
                      <strong style={{ color: '#0f172a' }}>{f.label}</strong>
                      <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '2px' }}>{f.desc}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="ck-features-col pro">
              <h3>Business operations</h3>
              <p className="ck-features-col-sub">Pitch to payment — actually get paid what you're worth.</p>
              <ul className="ck-features-list">
                {PRO_FEATURES.map(f => (
                  <li key={f.label}>
                    <span className="fcheck">✓</span>
                    <div>
                      <strong style={{ color: '#0f172a' }}>{f.label}</strong>
                      <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '2px' }}>{f.desc}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ─────────────────────────────────────────── */}
        <section className="ck-how" id="how">
          <div className="ck-section-head">
            <div className="ck-section-eyebrow">Pitch → Deliver → Get Paid</div>
            <h2>One workflow from first DM to bank deposit.</h2>
            <p>No tool-switching. No copy-pasting. No "wait, did we ever invoice them?"</p>
          </div>

          <div className="ck-how-grid">
            <div className="ck-how-step">
              <div className="ck-how-num">01</div>
              <h3>Pitch</h3>
              <p>Find brands, send your rate card and media kit, track every reply. Stop pitching from scratch every time.</p>
              <div className="ck-how-tools">
                <span className="ck-how-tool">Outreach</span>
                <span className="ck-how-tool">Rate Card</span>
                <span className="ck-how-tool">Media Kit</span>
              </div>
            </div>
            <div className="ck-how-step">
              <div className="ck-how-num">02</div>
              <h3>Deliver</h3>
              <p>Plan the calendar, generate ideas, write scripts and captions, ship on schedule. AI handles the busywork.</p>
              <div className="ck-how-tools">
                <span className="ck-how-tool">Calendar</span>
                <span className="ck-how-tool">Ideas</span>
                <span className="ck-how-tool">AI Captions</span>
                <span className="ck-how-tool">Repurpose</span>
              </div>
            </div>
            <div className="ck-how-step">
              <div className="ck-how-num">03</div>
              <h3>Get Paid</h3>
              <p>One-click contract, GST-ready invoice, automatic payment tracking. The money actually lands.</p>
              <div className="ck-how-tools">
                <span className="ck-how-tool">Contracts</span>
                <span className="ck-how-tool">Invoices</span>
                <span className="ck-how-tool">Payment Tracker</span>
              </div>
            </div>
          </div>

          <div className="ck-how-cta-row">
            {SHOW_DEMO_BUTTON && (
              <button type="button" className="ck-cta-watch" onClick={openDemo}>
                <span className="play">▶</span>
                See it in action
              </button>
            )}
            <button type="button" className="ck-cta-howfree" onClick={openSignUp}>
              Try it free →
            </button>
          </div>
        </section>

        {/* ── FINAL CTA ────────────────────────────────────────────── */}
        <section className="ck-final-cta">
          <div className="ck-final-cta-inner">
            <h2>
              Stop leaving<br />
              <span className="grad">money on the table.</span>
            </h2>
            <p>
              Join the Indian creators running their entire business in one app.
              No card. No contract. Cancel any time.
            </p>
            <div className="ck-final-cta-row">
              <button type="button" className="ck-final-primary" onClick={openSignUp}>
                Start free — no card needed →
              </button>
              <Link to="/contact" className="ck-final-secondary">
                Talk to founder
              </Link>
            </div>
          </div>
        </section>

        {/* ── INFO LINKS STRIP ─────────────────────────────────────── */}
        <section className="ck-info-strip">
          <div className="ck-info-strip-inner">
            <span className="ck-info-label">Resources & Trust</span>
            <div className="ck-info-links">
              <a href="/#features">Features</a>
              <span className="ck-info-sep">·</span>
              <a href="/blog">Blog</a>
              <span className="ck-info-sep">·</span>
              <Link to="/contact">Contact</Link>
              <span className="ck-info-sep">·</span>
              <Link to="/terms">Terms</Link>
              <span className="ck-info-sep">·</span>
              <Link to="/privacy">Privacy</Link>
              <span className="ck-info-sep">·</span>
              <Link to="/refund">Refund Policy</Link>
              <span className="ck-info-sep">·</span>
              <Link to="/cancellation">Cancellation</Link>
            </div>
          </div>
        </section>

        <Footer />

        <DemoModal open={demoOpen} onClose={closeDemo} />
      </div>
    </>
  );
}
